import * as React from 'react';
import { observable } from "mobx";
import { observer } from "mobx-react";
import autobind from "autobind-decorator";
import { Button, Modal, Col, Row } from 'antd';

import { DataLocalService } from 'src/core/services/data-local.service';
import { TextView } from 'src/core/ui/text.view';
import { BooleanView } from "src/core/ui/boolean.view";
import { CommandBar } from "src/core/ui/command-bar";
import { DataTableView, Column, ToolbarVisibility } from "src/core/ui/data-table.view";
import { TooltipView } from "src/core/ui/tooltip.view";
import * as uuid from 'uuid';
import { ToastNotifications } from 'src/core/ui/toast';
import { resolve } from 'inversify-react';

export type SearchType = 'Accommodation' | 'CarRent' | 'Transfer' | 'Flights' | 'General' | 'Tour';

interface SearchOccupancyProps {
	paxs: PersonOccupancy[];
	occupancies: Occupancy[];
	type: SearchType ;
	setBusy(isBusy: boolean);
	disabledEdit?: boolean;
	onDelete?: () => void;
	onEdit?: () => void;
}

@observer
export class PaxOccupancyView extends React.Component<SearchOccupancyProps, any> {
	@observable
	newPax: PersonOccupancy = { name: '', age: 30, id: uuid.v4() };

	@observable
	showDialog: boolean = false;

	@resolve
	public toast: ToastNotifications;

	private get columns(): Column[] {
		let firstColumn;
		switch (this.props.type) {
			case 'Accommodation':
				{
					firstColumn = "Rooms";
					break;
				}
			case 'CarRent':
				{
					firstColumn = "Cars";
					break;
				}
			case 'Transfer':
			case 'General':
				{
					firstColumn = "Units";
					break;
				}
			case 'Flights':
				{
					firstColumn = "Flights";
					break;
				}
		}
		return [{
			key: 'unit',
			title: firstColumn,
			dataIndex: 'unit',
			width: 75,
		}].concat(this.props.paxs.map(e => ({
			key: e.id.toString(),
			title: <TooltipView title={`${e.name} (${e.age})`}>
				<span style={{ display: 'block', textAlign: 'center' }}>{e.name}</span>
				<div style={{ display: 'flex', justifyContent: 'space-between' }}>
					{!this.props.disabledEdit && < Button style={{ alignSelf: 'flex-start' }} icon='edit' shape='circle' size="small" onClick={() => this.editPax(e)} />}
					<Button style={{ alignSelf: 'flex-end' }} icon='user-delete' type='danger' shape='circle' size="small" onClick={() => this.deletePax(e)} />
				</div>
			</TooltipView>,
			dataIndex: e.id.toString(),
			render: (text: string, record: any, index: number) => {
				return <BooleanView
					size='small'
					mode='edit'
					value={record[e.id]}
					onChanged={newVal => {
						record[e.id] = newVal;
						const occupancy = this.props.occupancies.find(o => o.unit == record.unit);
						if (newVal) {
							const pax = this.props.paxs.find(p => p.id == e.id);
							occupancy.paxs.push(pax);
							this.forceUpdate();
						} else {
							const paxIndex = occupancy.paxs.findIndex(p => p.id == e.id);
							occupancy.paxs.splice(paxIndex, 1);
						}

						if (this.props.onEdit)
							this.props.onEdit();
					}}
				/>
			},
			width: 100,
		})));
	}

	private get dataSet(): DataLocalService<any> {
		return new DataLocalService(this.props.occupancies.map(e => {
			let row = {
				unit: e.unit
			};
			for (const pax of this.props.paxs) {
				row[pax.id] = e.paxs.some(p => p != null && p.id == pax.id);
			}
			return row;
		}));
	}

	public constructor(props) {
		super(props);
	}

	@autobind
	protected createOccupancy() {
		let max = 0;
		for (const occupancy of this.props.occupancies) {
			if (occupancy.unit > max) {
				max = occupancy.unit;
			}
		}
		this.props.occupancies.push({ unit: max + 1, paxs: [] });
		return Promise.resolve(this.props.occupancies);
	}

	public deleteOcupancy(item: Occupancy) {
		const index = this.props.occupancies.findIndex(e => e.unit == item.unit && e.paxs.every(p => item[p.id]));
		this.props.occupancies.splice(index, 1);

	}

	@autobind
	deletePax(pax: PersonOccupancy) {
		for (let i = 0; i < this.props.occupancies.length; i++) {
			for (let y = 0; y < this.props.occupancies[i].paxs.length; y++)
				if (this.props.occupancies[i].paxs[y].id == pax.id) {
					this.props.occupancies[i].paxs.splice(y, 1);
					break;
				}
		}
		let indexPax = this.props.paxs.findIndex(x => x.id == pax.id);
		if (indexPax != -1)
			this.props.paxs.splice(indexPax, 1);

		if (this.props.onDelete)
			this.props.onDelete();
	}

	@autobind
	editPax(pax: PersonOccupancy) {
		this.newPax = {
			id: pax.id,
			name: pax.name,
			age: pax.age
		}
		this.showDialog = true;

	}

	@autobind
	addEdtiPax() {
		let pax = this.props.paxs.find(x => x.id == this.newPax.id);
		if (pax == null)
			this.props.paxs.push({ name: this.newPax.name, age: this.newPax.age, id: this.newPax.id });
		else {
			pax.name = this.newPax.name;
			pax.age = this.newPax.age;
		}


		if (this.props.onEdit)
			this.props.onEdit();
	}

	@autobind
	isPaxValid(pax: PersonOccupancy) {
		let errors = [];
		if (pax.name == '')
			errors.push("The name is required");
		if (isNaN(pax.age))
			errors.push("The age is required");
		return errors;
	}

	@autobind
	private async confirmDialog(ev?: React.MouseEvent<HTMLButtonElement>) {
		const errors = this.isPaxValid(this.newPax);
		if (errors.length == 0) {
			this.addEdtiPax()
			this.closeDialog(ev);
		} else {
			this.toast.showToast('Validation error!', errors, 'error');
		}
	}

	@autobind
	private closeDialog(ev?: React.MouseEvent<HTMLButtonElement>) {
		ev.preventDefault();
		this.newPax = {
			id: uuid.v4(),
			name: '',
			age: 30
		};
		this.showDialog = false;
	}

	public render() {
		if (this.props.occupancies.length > 0 && this.props.paxs.length > 0) {
			return (
				<div >
					<Modal
						visible={this.showDialog}
						onOk={this.confirmDialog}
						onCancel={this.closeDialog}
						title='Edit Pax'
						wrapClassName="vertical-center-modal"
						width={820}
						maskClosable={false}
						footer={[
							<Button key='add-pax' type="primary" onClick={this.confirmDialog}>Confirm</Button>]
						}
					>
						<Row>
							<Col xs={12} sm={12} md={12} lg={12}>
								<TextView
									required
									mode={"edit"}
									label='Display name'
									value={this.newPax.name}
									onChanged={newVal => this.newPax.name = newVal}
								/>
							</Col>
							<Col xs={12} sm={12} md={12} lg={12}>
								<TextView
									required
									mode={"edit"}
									label='Age'
									value={this.newPax.age}
									onChanged={newVal => this.newPax.age = parseInt(newVal)}
								/>
							</Col>
						</Row>
					</Modal>
					{<Row >
							<Col xs={24} sm={24} md={24} lg={24}>
								<DataTableView
									rowKey='unit'
									toolbarLeftItems={[
										{
											icon: 'user-add',
											key: 'add-pax',
											name: 'Add pax',
											onClick: () => this.showDialog = true
										}
									]}
									mode='edit'
									setBusy={this.props.setBusy}
									columns={this.columns}
									model={this.dataSet}
									createNewItem={this.createOccupancy}
									hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hidePagination}
									afterDeleteItem={items => {
										for (const item of items) {
											this.deleteOcupancy(item);
										}
									}}
								/>
							</Col>
						</Row>
					}
				</div>
			)
		}
		return null;
	}
}



// WEBPACK FOOTER //
// ./src/app/products/contractSimulator/parts/pax-occupancy.view.tsx